import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Form,
  Button,
  Dropdown,
  Badge,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";

const BoostManagement = () => {
  const [boosts, setBoosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    boost_type: "",
    status: "",
  });
  const [sorting, setSorting] = useState({
    sort_by: "created_at",
    sort_order: "desc",
  });
  const [stats, setStats] = useState({
    totalBoosts: 0,
    totalRevenue: 0,
    totalCost: 0,
    profit: 0,
    statusCounts: {},
    typeCounts: {},
  });

  // Fetch boosts with current filters and sorting
  const fetchBoosts = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();

      // Add filters if they exist
      if (filters.boost_type)
        queryParams.append("boost_type", filters.boost_type);
      if (filters.status) queryParams.append("status", filters.status);

      // Add sorting
      queryParams.append("sort_by", sorting.sort_by);
      queryParams.append("sort_order", sorting.sort_order);

      const response = await axiosClient.get(
        `https://api.viralgpt.com/admin/boosts?${queryParams.toString()}`,
      );

      setBoosts(response.data);
      calculateStats(response.data);
    } catch (error) {
      console.error("Error fetching boosts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics from boost data
  const calculateStats = (boostData) => {
    const stats = {
      totalBoosts: boostData.length,
      totalRevenue: 0,
      totalCost: 0,
      profit: 0,
      statusCounts: {},
      typeCounts: {},
    };

    boostData.forEach((boost) => {
      // Calculate financial metrics
      stats.totalRevenue += parseFloat(boost.price) || 0;
      stats.totalCost += parseFloat(boost.cost) || 0;

      // Count by status
      stats.statusCounts[boost.status] =
        (stats.statusCounts[boost.status] || 0) + 1;

      // Count by boost type
      const boostType = boost.boost_type;
      stats.typeCounts[boostType] = (stats.typeCounts[boostType] || 0) + 1;
    });

    stats.profit = stats.totalRevenue - stats.totalCost;
    setStats(stats);
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  // Handle sorting changes
  const handleSortChange = (column) => {
    setSorting({
      sort_by: column,
      sort_order:
        sorting.sort_by === column && sorting.sort_order === "asc"
          ? "desc"
          : "asc",
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      boost_type: "",
      status: "",
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Completed":
        return "success";
      case "Pending":
        return "warning";
      case "In Progress":
        return "primary";
      case "Partial":
        return "info";
      case "Canceled":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Get platform icon
  const getPlatformIcon = (boostType) => {
    if (!boostType) return "fa-question";

    const platform = boostType.split("-")[0];
    switch (platform) {
      case "instagram":
        return "fa-instagram";
      case "tiktok":
        return "fa-tiktok";
      case "youtube":
        return "fa-youtube";
      case "twitter":
        return "fa-twitter";
      default:
        return "fa-globe";
    }
  };

  // Get action name from boost type
  const getActionName = (boostType) => {
    if (!boostType) return "";

    const parts = boostType.split("-");
    return parts.length > 1
      ? parts[1].charAt(0).toUpperCase() + parts[1].slice(1)
      : "";
  };

  // Load boosts when component mounts or filters/sorting change
  useEffect(() => {
    fetchBoosts();
  }, [filters, sorting]);

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title>Boost Stats</Card.Title>
              <div className="d-flex flex-column gap-2 mt-3">
                <div className="d-flex justify-content-between">
                  <span>Total Boosts:</span>
                  <span className="fw-bold">{stats.totalBoosts}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Total Revenue:</span>
                  <span className="fw-bold text-success">
                    ${stats.totalRevenue.toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Total Cost:</span>
                  <span className="fw-bold text-danger">
                    ${stats.totalCost.toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Profit:</span>
                  <span
                    className={`fw-bold ${stats.profit >= 0 ? "text-success" : "text-danger"}`}
                  >
                    ${stats.profit.toFixed(2)}
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title>Status Distribution</Card.Title>
              <div className="d-flex flex-column gap-2 mt-3">
                {Object.entries(stats.statusCounts).map(([status, count]) => (
                  <div
                    key={status}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <Badge bg={getStatusBadgeColor(status)}>{status}</Badge>
                    <span>
                      {count} ({((count / stats.totalBoosts) * 100).toFixed(1)}
                      %)
                    </span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title>Boost Type Distribution</Card.Title>
              <div className="d-flex flex-column gap-2 mt-3">
                {Object.entries(stats.typeCounts).map(([type, count]) => (
                  <div
                    key={type}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <i className={`fab ${getPlatformIcon(type)} me-2`}></i>
                      {type}
                    </div>
                    <span>
                      {count} ({((count / stats.totalBoosts) * 100).toFixed(1)}
                      %)
                    </span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Boost Filters</Card.Title>
          <Row className="mt-3">
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Boost Type</Form.Label>
                <Form.Select
                  value={filters.boost_type}
                  onChange={(e) =>
                    handleFilterChange("boost_type", e.target.value)
                  }
                >
                  <option value="">All Types</option>
                  {Object.keys(stats.typeCounts).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {Object.keys(stats.statusCounts).map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4} className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                className="mb-3 w-100"
                onClick={resetFilters}
              >
                Reset Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Card.Title>Boost Orders</Card.Title>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <>
              {/* Desktop view */}
              <div className="table-responsive d-none d-md-block">
                <Table hover className="mt-3">
                  <thead>
                    <tr>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("id")}
                      >
                        ID
                        {sorting.sort_by === "id" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("userid")}
                      >
                        User ID
                        {sorting.sort_by === "userid" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("boost_type")}
                      >
                        Type
                        {sorting.sort_by === "boost_type" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                      <th>Link</th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("quantity")}
                      >
                        Quantity
                        {sorting.sort_by === "quantity" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("price")}
                      >
                        Price
                        {sorting.sort_by === "price" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("status")}
                      >
                        Status
                        {sorting.sort_by === "status" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => handleSortChange("created_at")}
                      >
                        Date
                        {sorting.sort_by === "created_at" && (
                          <i
                            className={`ms-1 fas fa-sort-${sorting.sort_order === "asc" ? "up" : "down"}`}
                          ></i>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {boosts.map((boost) => (
                      <tr key={boost.id}>
                        <td>{boost.id}</td>
                        <td>{boost.userid}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <i
                              className={`fab ${getPlatformIcon(boost.boost_type)} me-2`}
                            ></i>
                            {getActionName(boost.boost_type)}
                          </div>
                        </td>
                        <td>
                          <a
                            href={boost.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-truncate d-inline-block"
                            style={{ maxWidth: "200px" }}
                            title={boost.link}
                          >
                            {boost.link}
                          </a>
                        </td>
                        <td>{boost.quantity}</td>
                        <td>${parseFloat(boost.price).toFixed(2)}</td>
                        <td>
                          <Badge bg={getStatusBadgeColor(boost.status)}>
                            {boost.status}
                          </Badge>
                        </td>
                        <td>{formatDate(boost.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile view */}
              <div className="d-md-none">
                {boosts.map((boost) => (
                  <Card key={boost.id} className="mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">ID: {boost.id}</span>
                        <span className="ms-2 text-muted">
                          User: {boost.userid}
                        </span>
                        <Badge bg={getStatusBadgeColor(boost.status)}>
                          {boost.status}
                        </Badge>
                      </div>

                      <div className="mb-2">
                        <i
                          className={`fab ${getPlatformIcon(boost.boost_type)} me-2`}
                        ></i>
                        <span className="fw-bold">
                          {getActionName(boost.boost_type)}
                        </span>
                      </div>

                      <div className="mb-2 text-truncate">
                        <small className="text-muted">Link: </small>
                        <a
                          href={boost.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {boost.link}
                        </a>
                      </div>

                      <div className="d-flex justify-content-between mb-2">
                        <div>
                          <small className="text-muted">Quantity: </small>
                          {boost.quantity}
                        </div>
                        <div>
                          <small className="text-muted">Price: </small>$
                          {parseFloat(boost.price).toFixed(2)}
                        </div>
                      </div>

                      <div className="text-muted small">
                        {formatDate(boost.created_at)}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>

              {boosts.length === 0 && (
                <div className="text-center my-4">
                  <p>No boost orders found matching the current filters.</p>
                  <Button variant="outline-primary" onClick={resetFilters}>
                    Reset Filters
                  </Button>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default BoostManagement;
